import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "w-auto h-auto" }

import {
  computed,
  ref,
  watch,
  onMounted,
} from "vue";
import {
  Point,
  ImageNodeConfig,
  BoundingBox,
  MiddlePoint,
} from "@/types/stage";
import Konva from "konva";
import LoShooLines from "@/views/Calculators/ApartmentPlan/components/stage/LoShooLines.vue";
import MiddlePointComponent from "@/views/Calculators/ApartmentPlan/components/stage/MiddlePointComponent.vue";
import MiddleMountains from "@/views/Calculators/ApartmentPlan/components/stage/MiddleMountains.vue";
import BorderMountains from "@/views/Calculators/ApartmentPlan/components/stage/BorderMountains.vue";
import MountainsLabels from "@/views/Calculators/ApartmentPlan/components/stage/MountainsLabels.vue";
import PolygonPointsLayer from "@/views/Calculators/ApartmentPlan/components/stage/PolygonPointsLayer.vue";
import DiagramImageLayer from "@/views/Calculators/ApartmentPlan/components/stage/DiagramImageLayer.vue";
import AzimuthArrow from "@/views/Calculators/ApartmentPlan/components/stage/AzimuthArrow.vue";
import ApartmentFlyingStarsLayer from "@/views/Calculators/ApartmentPlan/components/stage/ApartmentFlyingStarsLayer.vue";
import {
  baseStarsAngles,
  ContainerProperties,
  useStarsPositions,
} from "@/composables/useStarsPositions";
import { AptDateParamsResponse, IImageConfig } from "@/types/apartmentPlan";
import { ToggleKey } from "@/constants/apartmentPlan";
import diagramImageSrc from "@/assets/calculators/room-mapping/diagram.png";

import Circle = Konva.Circle;
import Stage = Konva.Stage;

export default /*@__PURE__*/_defineComponent({
  __name: 'StageComponent',
  props: {
    stageWidth: {},
    stageHeight: {},
    azmFacade: {},
    buildYear: {},
    facadeDirection: {},
    points: {},
    imageNode: {},
    diagramDateParams: {},
    diagramScale: {},
    movePointsMode: { type: Boolean },
    toggles: {}
  },
  emits: ["point:add", "point:move", "point:remove"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const stageRef = ref(null);
__expose({
  stageRef,
});

const diagramImage = ref<HTMLImageElement | null>(null);

function loadDiagramImageAsset() {
  const img = new Image();
  img.onload = () => {
    diagramImage.value = img;
  };
  img.src = diagramImageSrc;
}

onMounted(() => {
  loadDiagramImageAsset();
});

const boundingBox = computed<BoundingBox | null>(() => {
  if (props.points.length < 4) return null;

  // 1. Найти крайние координаты
  const xs = props.points.map((p) => p.x);
  const ys = props.points.map((p) => p.y);

  const expandFactor = 30;
  const minX = Math.min(...xs) - expandFactor;
  const minY = Math.min(...ys) - expandFactor;
  const maxX = Math.max(...xs) + expandFactor;
  const maxY = Math.max(...ys) + expandFactor;

  // 2. Размеры прямоугольника
  const width = maxX - minX;
  const height = maxY - minY;

  return {
    minX: minX,
    minY: minY,
    maxX: maxX,
    maxY: maxY,
    width: width,
    height: height,
  };
});

const middlePoint = computed<MiddlePoint | null>(() => {
  if (!boundingBox.value) return null;

  const { minX, minY, width, height } = boundingBox.value;

  return {
    x: minX + width / 2,
    y: minY + height / 2,
  };
});

const rotateAngle = computed(() => {
  if (props.facadeDirection === null) {
    return props.azmFacade;
  }
  return props.facadeDirection - props.azmFacade;
});

const diagramImageConfig = computed<IImageConfig | null>(() => {
  if (!boundingBox.value || !middlePoint.value) {
    return null;
  }

  const { width, height } = boundingBox.value;
  const { x, y } = middlePoint.value;

  let boxSize = Math.max(width, height);
  const maxX = x - boxSize / 2 + boxSize;
  if (maxX > props.stageWidth) {
    boxSize -= maxX - props.stageWidth;
  } else if (maxX > props.stageHeight) {
    boxSize -= maxX - props.stageHeight;
  }
  const maxY = y - boxSize / 2 + boxSize;
  if (maxY > props.stageHeight) {
    boxSize -= maxY - props.stageHeight;
  } else if (maxY > props.stageWidth) {
    boxSize -= maxY - props.stageWidth;
  }
  const facadeDirection = props.facadeDirection ?? 0;

  return {
    image: diagramImage.value,
    x: x,
    y: y,
    width: boxSize,
    height: boxSize,
    offsetY: boxSize / 2,
    offsetX: boxSize / 2,
    rotation: facadeDirection - props.azmFacade,
    scaleX: props.diagramScale,
    scaleY: props.diagramScale,
  };
});

const getStarsContainerProps = computed<ContainerProperties | null>(() => {
  if (!diagramImageConfig.value) {
    return null;
  }
  const { x, y, width } = diagramImageConfig.value;
  return {
    centerX: x,
    centerY: y,
    radius: (width / 2) * props.diagramScale + 50,
  };
});

const { starsPositions, getStarIndex, centerAngle } = useStarsPositions(
  rotateAngle,
  () => getStarsContainerProps.value
);

watch(
  () => props.toggles["lo-shoo"],
  (showLoShoo: boolean) => {
    if (!showLoShoo) {
      centerAngle.value = 0;
      return;
    }
    let minDelta = Infinity;
    let targetAngle = Infinity;
    for (const baseAngle of baseStarsAngles) {
      const angleDelta = Math.abs(baseAngle - props.azmFacade);
      if (angleDelta < minDelta) {
        minDelta = angleDelta;
        targetAngle = baseAngle;
      }
    }
    centerAngle.value = props.azmFacade - targetAngle;
  }
);

function onStageClick(e: Stage) {
  if (e.evt && e.evt.button === 0) {
    emit("point:add", e);
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_rect = _resolveComponent("v-rect")!
  const _component_v_layer = _resolveComponent("v-layer")!
  const _component_v_image = _resolveComponent("v-image")!
  const _component_v_stage = _resolveComponent("v-stage")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_v_stage, {
      ref_key: "stageRef",
      ref: stageRef,
      config: { width: __props.stageWidth, height: __props.stageHeight },
      onClick: onStageClick,
      onTap: _cache[2] || (_cache[2] = (e) => emit('point:add', e)),
      onContextmenu: _cache[3] || (_cache[3] = (e) => e.evt.preventDefault())
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_layer, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_rect, {
              config: {
            x: 0,
            y: 0,
            width: __props.stageWidth,
            height: __props.stageHeight,
            fill: '#FFFFFF',
          }
            }, null, 8, ["config"])
          ]),
          _: 1
        }),
        _createVNode(_component_v_layer, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_image, { config: __props.imageNode }, null, 8, ["config"])
          ]),
          _: 1
        }),
        _createVNode(PolygonPointsLayer, {
          points: __props.points,
          movePointsMode: __props.movePointsMode,
          "onPoint:move": _cache[0] || (_cache[0] = (i, e) => emit('point:move', i, e)),
          "onPoint:remove": _cache[1] || (_cache[1] = (i, e) => emit('point:remove', i, e))
        }, null, 8, ["points", "movePointsMode"]),
        _createVNode(_component_v_layer, null, {
          default: _withCtx(() => [
            _createVNode(MiddlePointComponent, { middlePoint: middlePoint.value }, null, 8, ["middlePoint"]),
            (__props.toggles['lo-shoo'])
              ? (_openBlock(), _createBlock(LoShooLines, {
                  key: 0,
                  boundingBox: boundingBox.value
                }, null, 8, ["boundingBox"]))
              : _createCommentVNode("", true),
            (__props.toggles['middle-12-mountains'])
              ? (_openBlock(), _createBlock(MiddleMountains, {
                  key: 1,
                  boundingBox: boundingBox.value
                }, null, 8, ["boundingBox"]))
              : _createCommentVNode("", true),
            (__props.toggles['border-12-mountains'])
              ? (_openBlock(), _createBlock(BorderMountains, {
                  key: 2,
                  boundingBox: boundingBox.value
                }, null, 8, ["boundingBox"]))
              : _createCommentVNode("", true),
            (
            __props.toggles['middle-12-mountains'] || __props.toggles['border-12-mountains']
          )
              ? (_openBlock(), _createBlock(MountainsLabels, {
                  key: 3,
                  moveLabelsMode: __props.toggles['move-mountains-labels'],
                  boundingBox: boundingBox.value
                }, null, 8, ["moveLabelsMode", "boundingBox"]))
              : _createCommentVNode("", true),
            _createVNode(AzimuthArrow, {
              facadeDirection: __props.facadeDirection,
              boundingBox: boundingBox.value,
              middlePoint: middlePoint.value
            }, null, 8, ["facadeDirection", "boundingBox", "middlePoint"])
          ]),
          _: 1
        }),
        _createVNode(_component_v_layer, null, {
          default: _withCtx(() => [
            _createVNode(DiagramImageLayer, {
              diagramConfig: diagramImageConfig.value,
              diagramDateParams: __props.diagramDateParams,
              toggles: __props.toggles
            }, null, 8, ["diagramConfig", "diagramDateParams", "toggles"]),
            (__props.buildYear && __props.facadeDirection !== null)
              ? (_openBlock(), _createBlock(ApartmentFlyingStarsLayer, {
                  key: 0,
                  buildYear: __props.buildYear,
                  azimuthFacade: __props.azmFacade,
                  getStarIndex: _unref(getStarIndex),
                  starsPositions: _unref(starsPositions),
                  aptStars: 
            __props.toggles['current-stars'] ? __props.diagramDateParams?.stars : undefined
          
                }, null, 8, ["buildYear", "azimuthFacade", "getStarIndex", "starsPositions", "aptStars"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["config"])
  ]))
}
}

})